import { zodResolver } from "@hookform/resolvers/zod";
import { cn } from "@synthesis/shared-utils";
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";

import {
  adjacentObjectDataSchema,
  isAdjacentLandSectionRequired,
  scrollToFirstInvalidCreateApplicationField,
  usePageStore,
  type AdjacentObjectDataFormValues,
} from "../../create-application-page.lib";
import { AdjacentObjectCargoSection } from "./create-application-adjacent-object-cargo-section";
import { AdjacentObjectCommonSection } from "./create-application-adjacent-object-common-section";
import { AdjacentObjectConnectionSection } from "./create-application-adjacent-object-connection-section";
import { AdjacentObjectLandSection } from "./create-application-adjacent-object-land-section";
import { AdjacentObjectScheduleSection } from "./create-application-adjacent-object-schedule-section";

type CreateApplicationAdjacentObjectDataStepProps = {
  className?: string;
};

export const CreateApplicationAdjacentObjectDataStep = observer(({ className }: CreateApplicationAdjacentObjectDataStepProps) => {
  const pageStore = usePageStore();

  const methods = useForm<AdjacentObjectDataFormValues>({
    resolver: zodResolver(adjacentObjectDataSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: pageStore?.adjacentObjectData,
  });

  const { handleSubmit, watch, formState } = methods;
  const plannedWorkType = watch("plannedWorkType");
  const isLandSectionVisible = isAdjacentLandSectionRequired(plannedWorkType);

  useEffect(() => {
    pageStore?.setObjectDataValidator(
      () =>
        new Promise<boolean>((resolve) => {
          void handleSubmit(
            () => resolve(true),
            () => {
              scrollToFirstInvalidCreateApplicationField();
              resolve(false);
            },
          )();
        }),
    );

    return () => pageStore?.setObjectDataValidator(null);
  }, [pageStore, handleSubmit]);

  useEffect(() => {
    pageStore?.setObjectDataStepValid(formState.isValid);
  }, [formState.isValid, pageStore]);

  useEffect(() => {
    const subscription = watch((values) => {
      pageStore?.setAdjacentObjectData(values as AdjacentObjectDataFormValues);
    });

    return () => subscription.unsubscribe();
  }, [pageStore, watch]);

  return (
    <FormProvider {...methods}>
      <form className={cn("bg-neutrals-white flex flex-col gap-8 rounded-[14px] px-6 py-8", className)}>
        <AdjacentObjectCommonSection />
        <AdjacentObjectLandSection isVisible={isLandSectionVisible} />
        <AdjacentObjectConnectionSection />
        <AdjacentObjectCargoSection />
        <AdjacentObjectScheduleSection />
      </form>
    </FormProvider>
  );
});
